/* ----------------- React --------------- */
import { useState } from "react";

import { useExamContext } from "@features/exam/contexts/ExamContext";
import { AppReviewDialog } from "@features/feedback/components/dialogs/AppReviewDialog";
import { SessionConfigDialog } from "@features/session-config/components/SessionConfigDialog";
/* ----------------- Hooks --------------- */
import { useDevice } from "@hooks/useDevice";
import BottomFab from "@memorang/ui/src/components/BottomFab";
/* ----------------- UI --------------- */
import Box from "@memorang/ui/src/components/Box";
import CustomAppbar from "components/CustomAppbar";
import { useHome } from "../hooks/useHome";
import { HomeView } from "./HomeView";

/* ----------------- Helpers --------------- */
import { isWeb } from "@helpers/platform";

/* ----------------- Router --------------- */
import { useLocalSearchParams, useRouter } from "expo-router";

import { events } from "@constants/tracking";
import CollectUserDetailsDialog from "@features/account/components/dialogs/CollectUserDetailsDialog";
import FreeQuizSessionDialog from "@features/auth/components/dialogs/FreeQuizSessionDialog";
import { getShowStudyPackViews } from "@helpers/exams";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
/* ----------------- Analytics --------------- */
import { trackCustomEvent } from "analytics";
import { ExamsFilterDialog } from "components/ExamsFilterDialog";
import SetExamDateContainer from "components/SetExamDateContainer";
import { useTranslation } from "react-i18next";
import { OLXExamEligibilityDialog } from "../../../components/OLXExamEligibilityDialog";
import AppContextBannerContainer from "../containers/AppContextBannerContainer";
import { PurchaseDialog } from "./PurchaseDialog";

export const HomeRouteContainer = () => {
	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();

	const [showExamsFilterDialog, setShowExamsFilterDialog] = useState(false);

	const toggleShowExamsFilterDialog = () => {
		setShowExamsFilterDialog((prev) => !prev);
	};
	const { isMobile, isMediumWindowSize } = useDevice();
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);

	const toggleShowPurchaseDialog = () => {
		setShowPurchaseDialog((prev) => !prev);
	};

	const { startDiagnostic } = useLocalSearchParams<{
		startDiagnostic: string;
	}>();

	const router = useRouter();

	const showStudyPackViews = getShowStudyPackViews(studyPackCollectionId);

	const [selectedWeakTags, setSelectedWeakTags] = useState<
		string[] | undefined
	>(undefined);

	const {
		sections,
		headerData,
		showPurchaseDialog: showPracticeTestPurchaseDialog,
		toggleShowPurchaseDialog: toggleShowPracticeTestPurchaseDialog,
		olxError,
		setOlxError,
		showCollectUserDetailsDialog,
		toggleShowCollectUserDetailsDialog,
		showDatePicker,
		toggleShowDatePicker,
		hasAccess,
		exams,
	} = useHome();

	const handleSessionConfigNavigation = (tagIds: number[]) => {
		if (isWeb) {
			setSelectedWeakTags(tagIds.map((id) => id.toString()));
		} else {
			router.push({
				pathname: "/session/customize",
				params: {
					selectedTagIds: tagIds,
				},
			});
		}
	};

	const handleQuickStudy = () => {
		trackCustomEvent({
			eventName: events.studyFabClicked,
		});
		router.push("/session/customize");
	};

	const showSessionConfigDialog = selectedWeakTags != null;

	const showBottomFab = (isMobile || isMediumWindowSize) && showStudyPackViews;

	const { handleExamsFilterNavigation } = useCustomNavigation();
	const handleSelectTopic = (topicId: number) => {
		handleSessionConfigNavigation([topicId]);
	};

	// const redeemableProducts = testRedeemableProducts;

	const showFreeQuizDialog = startDiagnostic === "true";

	const handleShowPurchase = () => {
		if (isWeb) {
			toggleShowPurchaseDialog?.();
		} else {
			router.push({
				pathname: "/purchase",
				params: {
					productType: studyPackCollectionId ? "STUDY_PACK" : "PRACTICE_TEST",
				},
			});
		}
	};
	const handleSwitchExam = () => {
		handleExamsFilterNavigation(toggleShowExamsFilterDialog);
	};
	const { t } = useTranslation();
	return (
		<Box position="relative" flexGrow={1} height={"100%"}>
			<CustomAppbar
				mode={isMobile ? "small" : "medium"}
				align="flex-start"
				showCommandBar={isWeb}
				options={{
					headerTitle: isWeb && isMobile ? "" : t("appbar.home.title"),
					subtitle: isWeb || isMobile ? "" : t("appbar.home.subtitle"),
				}}
				showHambugerMenu
				role="banner"
				accessible
				accessibilityLabel="App bar containing title, search bar, feedback link, settings link, and profile link"
			/>
			<Box flex={1}>
				<AppContextBannerContainer />
				<HomeView
					headerData={headerData}
					sections={sections}
					handleShowPurchase={handleShowPurchase}
					handleSelectTopic={handleSelectTopic}
					hasAccess={hasAccess}
					exams={exams}
					handleSwitchExam={handleSwitchExam}
					showPurchaseDialog={showPracticeTestPurchaseDialog}
					toggleShowPurchaseDialog={toggleShowPracticeTestPurchaseDialog}
				/>
			</Box>
			{showBottomFab && (
				<BottomFab
					icon={"play-outline"}
					label="Quick Study"
					onPress={handleQuickStudy}
					bottom={16}
				/>
			)}

			{showSessionConfigDialog ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					selectedTagIds={selectedWeakTags?.map((id) => Number(id))}
					handleClose={() => setSelectedWeakTags(undefined)}
				/>
			) : null}
			{!isWeb && <AppReviewDialog />}

			{showFreeQuizDialog && (
				<FreeQuizSessionDialog
					visible={showFreeQuizDialog}
					onClose={() => {
						router.setParams({
							startDiagnostic: "false",
						});
					}}
				/>
			)}
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={toggleShowPurchaseDialog}
					productType="STUDY_PACK"
				/>
			)}
			{!!olxError && (
				<OLXExamEligibilityDialog
					visible={!!olxError}
					handleClose={() => setOlxError?.(null)}
					title={olxError?.title}
					message={olxError?.message}
				/>
			)}
			{showCollectUserDetailsDialog && toggleShowCollectUserDetailsDialog && (
				<CollectUserDetailsDialog
					visible={showCollectUserDetailsDialog}
					handleClose={toggleShowCollectUserDetailsDialog}
				/>
			)}
			<SetExamDateContainer
				showDatePicker={showDatePicker}
				toggleShowDatePicker={toggleShowDatePicker}
			/>
			{showExamsFilterDialog && (
				<ExamsFilterDialog
					visible={showExamsFilterDialog}
					handleClose={toggleShowExamsFilterDialog}
				/>
			)}
		</Box>
	);
};
